import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getArchivedWorkOrders, getEquipmentTypes, unarchiveWorkOrder } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, 
  Archive,
  MapPin, 
  Calendar,
  ChevronRight,
  RefreshCw,
  RotateCcw,
  Filter,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';

const ArchivePage = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  
  // Search filters
  const [searchQuery, setSearchQuery] = useState('');
  const [woNumber, setWoNumber] = useState('');
  const [station, setStation] = useState('');
  const [equipmentType, setEquipmentType] = useState('all');
  const [assetType, setAssetType] = useState('all');
  const [projectType, setProjectType] = useState('all');

  useEffect(() => {
    fetchEquipmentTypes();
    fetchArchivedWorkOrders();
  }, []);

  const fetchEquipmentTypes = async () => {
    try {
      const response = await getEquipmentTypes();
      setEquipmentTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch equipment types:', error);
    }
  };

  const fetchArchivedWorkOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (woNumber) params.wo_number = woNumber;
      if (station) params.station = station;
      if (equipmentType !== 'all') params.equipment_type = equipmentType;
      if (assetType !== 'all') params.asset_type = assetType;
      if (projectType !== 'all') params.project_type = projectType;
      
      const response = await getArchivedWorkOrders(params);
      setWorkOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch archived work orders:', error);
      toast.error('Failed to load archived work orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchArchivedWorkOrders();
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setWoNumber('');
    setStation('');
    setEquipmentType('all');
    setAssetType('all');
    setProjectType('all');
    setTimeout(fetchArchivedWorkOrders, 100);
  };

  const handleUnarchive = async (woId) => {
    try {
      await unarchiveWorkOrder(woId);
      toast.success('Work Order restored from archive');
      fetchArchivedWorkOrders();
    } catch (error) {
      toast.error('Failed to restore work order');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800 border-amber-300',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      closed: 'bg-slate-100 text-slate-800 border-slate-300'
    };
    return colors[status] || colors.pending;
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="archive-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <Archive className="inline-block w-8 h-8 mr-2 text-[#0056D2]" />
            Work Order Archive
          </h1>
          <p className="text-slate-600">Search and review historical work orders</p>
        </div>
        <div className="text-sm text-slate-500">
          {workOrders.length} archived work order{workOrders.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Search Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            {/* Quick Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Quick search (WO#, title, location, station, equipment)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="archive-search-input"
              />
            </div>

            {/* Detailed Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <Input
                placeholder="Work Order #"
                value={woNumber}
                onChange={(e) => setWoNumber(e.target.value)}
                data-testid="wo-number-filter"
              />
              <Input
                placeholder="Station"
                value={station}
                onChange={(e) => setStation(e.target.value)}
                data-testid="station-filter"
              />
              <Select value={equipmentType} onValueChange={setEquipmentType}>
                <SelectTrigger data-testid="equipment-type-filter">
                  <SelectValue placeholder="Equipment Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Equipment</SelectItem>
                  {equipmentTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={assetType} onValueChange={setAssetType}>
                <SelectTrigger data-testid="asset-type-filter">
                  <SelectValue placeholder="Asset Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assets</SelectItem>
                  <SelectItem value="Transformer">Transformer</SelectItem>
                  <SelectItem value="Circuit Breaker">Circuit Breaker</SelectItem>
                  <SelectItem value="Disconnect Switch">Disconnect Switch</SelectItem>
                  <SelectItem value="Protection Relays">Protection Relays</SelectItem>
                  <SelectItem value="Bus Work">Bus Work</SelectItem>
                  <SelectItem value="Control Building">Control Building</SelectItem>
                  <SelectItem value="Battery System">Battery System</SelectItem>
                </SelectContent>
              </Select>
              <Select value={projectType} onValueChange={setProjectType}>
                <SelectTrigger data-testid="project-type-filter">
                  <SelectValue placeholder="Project Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  <SelectItem value="Equipment Replacement">Equipment Replacement</SelectItem>
                  <SelectItem value="Testing">Testing</SelectItem>
                  <SelectItem value="Emergency">Emergency</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                  <SelectItem value="New Installation">New Installation</SelectItem>
                  <SelectItem value="Inspection">Inspection</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 bg-[#0056D2] hover:bg-[#0044A4]" data-testid="search-archive-btn">
                  <Search className="w-4 h-4 mr-1" />
                  Search
                </Button>
                <Button type="button" variant="outline" onClick={handleClearFilters}>
                  Clear
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#0056D2]" />
        </div>
      ) : workOrders.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Archive className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold mb-2">No Archived Work Orders</h3>
            <p className="text-slate-500">
              {searchQuery || woNumber || station || equipmentType !== 'all' || assetType !== 'all' || projectType !== 'all'
                ? 'No results match your search criteria. Try adjusting your filters.'
                : 'Completed work orders will appear here when archived.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {workOrders.map((wo) => (
            <Card 
              key={wo.id}
              className="hover:border-[#0056D2] transition-all"
              data-testid={`archived-wo-${wo.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="font-mono text-sm text-[#0056D2] font-bold">
                        {wo.wo_number}
                      </span>
                      <Badge className={getStatusColor(wo.status)}>
                        {wo.status?.replace('_', ' ')}
                      </Badge>
                      <Badge variant="outline" className="bg-slate-100">
                        <Archive className="w-3 h-3 mr-1" />
                        Archived
                      </Badge>
                      {wo.equipment_type && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                          <Zap className="w-3 h-3 mr-1" />
                          {wo.equipment_type}
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-lg mb-1">{wo.title}</h3>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {wo.location_name}
                      </span>
                      {wo.stations?.length > 0 && (
                        <span>Stations: {wo.stations.join(', ')}</span>
                      )}
                      {wo.start_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {wo.start_date}
                        </span>
                      )}
                    </div>
                    {wo.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {wo.tags.slice(0, 5).map((tag, i) => (
                          <span key={i} className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {wo.archived_at && (
                      <p className="text-xs text-slate-400 mt-2">
                        Archived on {new Date(wo.archived_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Link to={`/work-orders/${wo.id}`}>
                      <Button variant="outline" size="sm">
                        View <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleUnarchive(wo.id)}
                      data-testid={`restore-wo-${wo.id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Restore
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ArchivePage;
